/**
 * ENG-7053 / ENG-7220 - rolling host-side session-tool-bind probe orchestration.
 *
 * Mirrors `connectivity-probe-runner`: given an agent's installed integrations,
 * selects the ones DUE for a probe (`last_session_tool_bind_at` older than the
 * interval, NULLS first), bounds the batch so we don't probe everything at once,
 * computes a per-integration `bound|missing|unreachable|unknown` verdict from
 * signals the manager already has, and returns the report batch to POST to
 * `/host/session-tool-bind`.
 *
 * Unlike the connectivity probe (which asks "are the credentials good / does the
 * managed account answer"), this asks the narrower runtime question: "are this
 * integration's MCP tools actually BOUND in the running agent session right now"
 * - a backing stdio child is alive, or a remote MCP answers `tools/list`. That
 * is the signal the Add Integration modal polls to confirm a newly-added
 * integration came online after the agent's next restart, instead of resolving
 * at "credentials verified".
 *
 * Pure + injectable (clock, interval, batch cap, the missing-stdio set, and the
 * HTTP reachability probe) so due-selection, key-resolution and verdict mapping
 * are unit-testable. The real ps-row gathering, `.mcp.json` read and the POST
 * live in the poll-loop wiring (`runAgentSessionToolBindProbes`).
 */

import {
  computeSessionToolBindStatus,
  splitServerKeysByKind,
  type SessionToolBindStatus,
} from './session-tool-probe.js';
import { isSessionToolBindCandidate } from '@augmented/core/integrations';
import { DEFAULT_COLD_START_GRACE_MS } from './mcp-presence-reaper.js';
import type { McpConfig } from './stale-mcp-reaper.js';

/** One installed integration as returned (enriched) by /host/agent-integrations. */
export interface SessionToolBindRunnerIntegration {
  id: string;
  definition_id: string;
  scope: 'agent' | 'team' | 'organization';
  last_session_tool_bind_at?: string | null;
  /**
   * Optional explicit `.mcp.json` server-key hint (e.g. from `deriveMcpServerKey`
   * for managed/remote-MCP kinds). Tried first when resolving the backing key.
   */
  mcp_server_key?: string | null;
  /**
   * ENG-9114: the toolkit delivery columns, enriched onto `/host/agent-integrations`.
   * They decide whether a zero-key resolution is PERMANENT (this integration has
   * no `.mcp.json` server — `not_applicable`) or TRANSIENT (an MCP integration
   * whose entry has not been rendered yet — stays unreported).
   *
   * All optional. An older API that does not send them leaves every field unset,
   * which classifies as `builtin` and would report `not_applicable` for rows that
   * are really MCP-backed — so the runner requires the payload to have spoken at
   * all before it will claim not-applicable. See `hasDeliverySignal` below.
   */
  source_type?: string | null;
  provider?: string | null;
  mcp_command?: string | null;
  mcp_url?: string | null;
  cli_binary?: string | null;
  cli_package?: string | null;
}

export interface SessionToolBindReport {
  integration_id: string;
  scope: 'agent' | 'team' | 'organization';
  status: SessionToolBindStatus;
}

/**
 * ENG-7318: an integration whose verdict is a hard-negative in-session state that
 * a targeted MCP-child reap can fix - `missing` (a dead stdio/proxy child) or
 * `unreachable` (an alive oauth-proxy child whose upstream stopped answering
 * `tools/list`). Carries the resolved child-backed `.mcp.json` keys so the caller
 * can SIGTERM exactly those children (Claude Code respawns them, no full restart).
 * The long-running manager acts on these only behind the `session-tool-rebind`
 * flag; the on-demand CLI "Probe now" ignores them (a probe never mutates state).
 * Pure-HTTP negatives produce no candidate (no child to respawn).
 */
export interface SessionToolRebindCandidate {
  integration_id: string;
  scope: 'agent' | 'team' | 'organization';
  status: 'missing' | 'unreachable';
  /** Child-backed server keys (stdio + oauth-proxy) to reap for a respawn. */
  serverKeys: string[];
}

export interface SessionToolBindRunnerOptions {
  now?: () => Date;
  /** A row is due when its last probe is older than this (default 1h). */
  intervalMs?: number;
  /** Max probes per run - bounds the rolling batch (default 25). */
  maxPerRun?: number;
  /** Parsed project `.mcp.json` (the declared MCP servers). */
  mcpJson: McpConfig | null | undefined;
  /**
   * Declared stdio server keys found to have NO live child this tick (from
   * `findMissingMcpServers`). Computed once over the whole `.mcp.json`.
   */
  missingStdioKeys: ReadonlySet<string>;
  /**
   * ENG-10601: false when NO declared stdio child is live - the session spawned
   * nothing, so "no tools bound" is a fact about the session, not about any
   * integration. Threaded straight through to `computeSessionToolBindStatus`,
   * which downgrades a would-be `missing` to `unknown` on it, and read again
   * below so the resulting skip is attributed to the right cause rather than
   * landing in the generic `inconclusive` bucket (ENG-9114 AC2).
   *
   * Computed by the host gather, which is the only place holding both the
   * declared set and the ps-derived missing set. Defaults to `true` so a caller
   * that does not supply it behaves exactly as before.
   */
  anyStdioServerLive?: boolean;
  /**
   * ENG-9048 AC3: server keys whose child is ALIVE but whose transport has
   * stopped carrying traffic — the failure `missingStdioKeys` cannot see,
   * because `ps` says the process is fine and it is.
   *
   * Only ever populated from a fresh, positive liveness signal published by the
   * server itself; absence of evidence must never land a key here, because
   * these keys go on to be SIGTERMed.
   *
   * A CALLBACK, not a set (CodeRabbit, #4680). Read once before the run and
   * held, a snapshot goes stale while the runner awaits probes for earlier
   * integrations: Slack can reconnect in that window, or the underlying file
   * can pass its freshness horizon, and the retained set would then SIGTERM a
   * healthy child. That is the failure this whole change exists to avoid,
   * arriving through the staleness of my own in-memory snapshot rather than
   * the staleness of the file it came from. Evaluated per integration, at the
   * moment the verdict and the rebind candidate are decided.
   */
  transportDownKeys?: () => ReadonlySet<string>;
  /**
   * Probe a declared HTTP/remote MCP server key for `tools/list` reachability.
   * `true` = answered (tools bound), `false` = unreachable/unauthorized,
   * `undefined` = couldn't probe this cycle (transient - don't penalise).
   */
  probeHttp: (serverKey: string) => Promise<boolean | undefined>;
  /**
   * The MCP server keys the running session loaded at spawn (ENG-7263, from the
   * persisted spawn-time snapshot). Gates HTTP `bound` per-server on membership,
   * so a reachable remote endpoint the session didn't load at spawn isn't
   * reported as live before the agent's restart. Defaults to empty (conservative:
   * a pure-HTTP integration stays `unknown` until we can prove the session loaded
   * it). Replaces the old session-start-vs-file-mtime gate.
   */
  sessionLoadedServerKeys?: ReadonlySet<string>;
  /**
   * ENG-8448: epoch ms the running session (re)spawned, or null when unknown. The
   * runner re-evaluates the cold-start grace (`nowMs - sessionStartedMs <
   * DEFAULT_COLD_START_GRACE_MS`) immediately before EACH integration's verdict,
   * off the injected clock - not once up-front - so a serial `probeHttp` that
   * crosses the grace boundary mid-run can't leave a later still-`missing`
   * stdio/proxy child wrongly suppressed as `unknown` (CodeRabbit on #4068).
   * Within the window a would-be `missing` becomes `unknown` (dropped, never
   * counted toward the ENG-7916 quarantine); null ⇒ no suppression.
   */
  sessionStartedMs?: number | null;
}

export interface SessionToolBindRunnerResult {
  /** Reports to POST up (only integrations with an ACTIONABLE verdict). */
  reports: SessionToolBindReport[];
  /**
   * ENG-7318: subset of reports whose negative verdict a targeted child reap can
   * fix, enriched with the child-backed server keys to reap. The manager acts on
   * these behind the `session-tool-rebind` flag; the CLI probe ignores them.
   */
  rebindCandidates: SessionToolRebindCandidate[];
  /** How many integrations were due this run. */
  due: number;
  /** How many were actually probed (bounded by maxPerRun). */
  probed: number;
  /**
   * Probed but produced no actionable verdict - an MCP integration whose
   * `.mcp.json` entry is not there yet, or an `unknown` we deliberately do not
   * write (so it can't regress a prior good state to NULL-equivalent).
   *
   * ENG-9114: this no longer includes CLI/builtin integrations. Those now report
   * `not_applicable` instead of silently vanishing from the batch.
   */
  skipped: number;
  /**
   * ENG-9114 AC2. The gap between `due` and `reported` WAS the entire signal and
   * it was unattributed: `probed=2 reported=2 due=4` told a reader four rows were
   * expected and two disappeared, with no way to learn which or why. Every row
   * that produced no report is named here with its reason, so the log line can
   * say what it skipped instead of leaving the reader to infer a failure.
   */
  skippedDetail: SessionToolBindSkip[];
  /** How many reported `not_applicable` (no `.mcp.json` server, permanently). */
  notApplicable: number;
}

/** One row that produced no bind report, and why (ENG-9114 AC2). */
export interface SessionToolBindSkip {
  definition_id: string;
  /**
   *   no-declared-server - an MCP-backed integration with no matching `.mcp.json`
   *                        key yet (provisioning has not rendered it, or the agent
   *                        has not restarted). Transient: it will resolve.
   *   inconclusive       - probed, but the verdict was `unknown`. Deliberately not
   *                        written, so it can't regress a prior good row.
   *   delivery-unknown   - the payload carried no delivery columns at all (an API
   *                        older than ENG-9114), so we cannot tell a CLI/builtin
   *                        row from an unrendered MCP one. Falls back to the old
   *                        behaviour: report nothing rather than guess.
   *   session-not-live   - ENG-10601: no declared stdio child is live at all, so
   *                        this session spawned nothing and the absence of tools
   *                        is evidence about the SESSION, not this integration.
   *                        Separated from `inconclusive` because the two need
   *                        opposite responses: an inconclusive probe is noise to
   *                        retry, a session that never started needs a human at
   *                        the pane, and collapsing them hides a fleet-wide
   *                        login outage inside routine probe chatter.
   */
  reason: 'no-declared-server' | 'inconclusive' | 'delivery-unknown' | 'session-not-live';
}

const DEFAULT_INTERVAL_MS = 60 * 60 * 1000; // 1 hour
const DEFAULT_MAX_PER_RUN = 25;

/**
 * Sanitised `definition_id`, matching the managed-toolkit `.mcp.json` writer and
 * `deriveMcpServerKey` (non-alphanumeric → '_', lowercased): `composio/stripe`
 * → `composio_stripe`.
 */
export function sanitizeServerKey(definitionId: string): string {
  return definitionId.replace(/[^a-z0-9]/gi, '_').toLowerCase();
}

/**
 * Resolve which declared `.mcp.json` server key(s) back an integration, by
 * intersecting deterministic candidates with the keys actually present. The
 * candidate set covers every wiring path without re-implementing the generator:
 *   • managed (Composio) / remote-MCP writer sanitises the definition_id
 *     (`composio/stripe` → `composio_stripe`, `granola` → `granola`);
 *   • the remote-OAuth / custom-header writer uses the raw definition_id
 *     (`anchor-browser`);
 *   • native MCP and the hardcoded specials (xero/postiz/cloud-broker) key on
 *     the definition_id (or a registry key passed as the `hint`).
 * An integration that matches nothing has no MCP server in the session
 * (direct-HTTP API, composio-account binding-only, builtin) → no verdict.
 */
export function resolveIntegrationServerKeys(
  definitionId: string,
  declaredKeys: readonly string[],
  hint?: string | null,
): string[] {
  const declared = new Set(declaredKeys);
  const candidates = [hint, definitionId, sanitizeServerKey(definitionId)].filter(
    (k): k is string => typeof k === 'string' && k.length > 0,
  );
  const out = new Set<string>();
  for (const c of candidates) if (declared.has(c)) out.add(c);
  return [...out];
}

/**
 * ENG-9114. Did `/host/agent-integrations` tell us ANYTHING about how this
 * integration is delivered?
 *
 * `classifyBindDelivery` falls through to `builtin` when every column is unset,
 * which is right for a real builtin and wrong for a row from an API too old to
 * send the columns. Claiming `not_applicable` off that silence would mislabel a
 * genuinely MCP-backed integration as "no MCP server, permanently" - the exact
 * shape of defect this ticket exists to end, so it must not be reintroduced by
 * the fix for it. Require the payload to have spoken first; if it did not, fall
 * back to the pre-ENG-9114 behaviour of reporting nothing.
 *
 * `source_type` alone satisfies this on every current API, so the fallback is a
 * genuine old-host guard rather than a permanent brake.
 */
function hasDeliverySignal(integ: SessionToolBindRunnerIntegration): boolean {
  // PRESENCE, not truthiness. An explicit `null` IS an answer - it says this
  // toolkit sets no `mcp_command`, which is precisely the evidence that makes a
  // builtin classifiable. Testing `!= null` would have thrown that answer away
  // and sent every all-null row (a builtin, the majority case here) down the
  // old-API fallback, so the fix for this ticket would have declined to fire on
  // most of the rows it was written for. (CodeRabbit)
  //
  // The caller must therefore OMIT keys it has nothing to say about rather than
  // set them to `undefined` - see `deliveryFields` in
  // session-tool-bind-probe-host.ts, which is the other half of this contract.
  return DELIVERY_FIELDS.some((k) => Object.prototype.hasOwnProperty.call(integ, k));
}

/** The delivery columns, named once so the runner and the host cannot drift. */
const DELIVERY_FIELDS = [
  'source_type',
  'provider',
  'mcp_command',
  'mcp_url',
  'cli_binary',
  'cli_package',
] as const satisfies ReadonlyArray<keyof SessionToolBindRunnerIntegration>;

function isDue(last: string | null | undefined, now: number, intervalMs: number): boolean {
  if (!last) return true; // never probed
  const t = Date.parse(last);
  if (Number.isNaN(t)) return true; // unparseable → treat as due
  return now - t >= intervalMs;
}

export async function runSessionToolBindProbes(
  integrations: SessionToolBindRunnerIntegration[],
  options: SessionToolBindRunnerOptions,
): Promise<SessionToolBindRunnerResult> {
  const now = (options.now?.() ?? new Date()).getTime();
  const intervalMs = options.intervalMs ?? DEFAULT_INTERVAL_MS;
  const maxPerRun = options.maxPerRun ?? DEFAULT_MAX_PER_RUN;

  // Due rows, oldest-first (NULLS first) so the rolling cursor clears the
  // backlog fairly and load spreads across ticks.
  const due = integrations
    .filter((i) => isDue(i.last_session_tool_bind_at, now, intervalMs))
    .sort((a, b) => {
      const ta = a.last_session_tool_bind_at ? Date.parse(a.last_session_tool_bind_at) : 0;
      const tb = b.last_session_tool_bind_at ? Date.parse(b.last_session_tool_bind_at) : 0;
      return ta - tb;
    });

  const batch = due.slice(0, maxPerRun);
  const declaredKeys = Object.keys(options.mcpJson?.mcpServers ?? {});
  const reports: SessionToolBindReport[] = [];
  const rebindCandidates: SessionToolRebindCandidate[] = [];
  let skipped = 0;
  let notApplicable = 0;
  const skippedDetail: SessionToolBindSkip[] = [];
  // Rows that reached an actual bind probe (resolved to >=1 backing MCP server).
  // Distinct from batch.length, which also counts rows skipped below for having
  // no declared server - counting those as "probed" would inflate rollout
  // metrics (CodeRabbit, PR #2860).
  let probed = 0;

  for (const integ of batch) {
    const keys = resolveIntegrationServerKeys(
      integ.definition_id,
      declaredKeys,
      integ.mcp_server_key,
    );
    if (keys.length === 0) {
      // No backing MCP server key resolved. ENG-9114: that has two causes and
      // they are not the same answer.
      if (!hasDeliverySignal(integ)) {
        // The API told us nothing about delivery, so we cannot tell them apart.
        // Report nothing, exactly as before this ticket.
        skipped += 1;
        skippedDetail.push({ definition_id: integ.definition_id, reason: 'delivery-unknown' });
        continue;
      }
      if (!isSessionToolBindCandidate({
        sourceType: integ.source_type,
        provider: integ.provider,
        mcpCommand: integ.mcp_command,
        mcpUrl: integ.mcp_url,
        cliBinary: integ.cli_binary,
        cliPackage: integ.cli_package,
      })) {
        // CLI-delivered or builtin: it has no `.mcp.json` server and never will,
        // so "bound" is not a question about it. Say so, rather than leaving the
        // NULL that six days of ENG-8751 read as a bind failure.
        notApplicable += 1;
        reports.push({
          integration_id: integ.id,
          scope: integ.scope,
          status: 'not_applicable',
        });
        continue;
      }
      // An MCP-backed integration whose `.mcp.json` entry is not there yet
      // (provisioning has not rendered it, or the agent has not restarted).
      // Genuinely "not probed yet" - stay quiet so the next run can answer.
      skipped += 1;
      skippedDetail.push({ definition_id: integ.definition_id, reason: 'no-declared-server' });
      continue;
    }
    probed += 1;

    const { stdio, http, proxy } = splitServerKeysByKind(options.mcpJson, keys);

    // Probe tools/list reachability for both direct-HTTP keys and oauth-proxy
    // keys (ENG-7318). For a proxy key, `probeHttp` reconstructs the upstream
    // {url, Authorization} from the proxy's baked env (readMcpHttpServerConfig),
    // so an alive-child/stale-upstream proxy reports the upstream as down.
    const httpReachable = new Map<string, boolean>();
    for (const key of [...http, ...proxy]) {
      let reachable: boolean | undefined;
      try {
        reachable = await options.probeHttp(key);
      } catch {
        reachable = undefined; // transient - leave unprobed
      }
      if (reachable !== undefined) httpReachable.set(key, reachable);
    }

    // ENG-8448: re-evaluate the cold-start grace HERE, per integration, off the
    // injected clock - NOT once up-front - so time spent in the serial `probeHttp`
    // awaits above (which can cross DEFAULT_COLD_START_GRACE_MS mid-run) can't
    // leave a later genuinely-missing child suppressed as `unknown` once real
    // elapsed time has left the window (CodeRabbit on #4068).
    const nowMs = (options.now?.() ?? new Date()).getTime();
    const coldStartActive =
      options.sessionStartedMs != null &&
      nowMs - options.sessionStartedMs < DEFAULT_COLD_START_GRACE_MS;

    // Fresh read at decision time - see the option's doc for why a snapshot is
    // not safe here. Read ONCE per integration and reused for both the verdict
    // and the candidate below, so those two cannot disagree about the same
    // moment.
    const transportDown = options.transportDownKeys?.();

    const status = computeSessionToolBindStatus({
      stdioServerKeys: stdio,
      httpServerKeys: http,
      proxyServerKeys: proxy,
      missingStdioKeys: options.missingStdioKeys,
      httpReachable,
      sessionLoadedServerKeys: options.sessionLoadedServerKeys ?? new Set<string>(),
      coldStartActive,
      ...(options.anyStdioServerLive !== undefined
        ? { anyStdioServerLive: options.anyStdioServerLive }
        : {}),
      ...(transportDown ? { transportDownKeys: transportDown } : {}),
    });

    // Don't persist 'unknown': it carries no actionable signal and writing it
    // would regress a previously-good row to a NULL-equivalent. The modal falls
    // back to its honest "applies on next restart" timeout state instead.
    if (status === 'unknown') {
      skipped += 1;
      // ENG-10601: attribute the skip, but only as far as the evidence reaches
      // (CodeRabbit on #6294). Two conditions, both required:
      //
      //   1. the session has no witness - it spawned no declared stdio child;
      //   2. THIS integration's unknown actually came from the missing-child
      //      branch, i.e. one of ITS OWN stdio/proxy keys is missing.
      //
      // Condition 1 alone is an agent-wide fact and over-claims. A pure-HTTP
      // integration whose reachable key the running session has not loaded yet
      // resolves to `unknown` through an entirely different branch; labelling
      // that `session-not-live` would blame the session for a pending state it
      // did not cause, on exactly the readout an operator would use to decide
      // whether to go and look at the pane. An attribution that is wrong is
      // worse than the generic bucket it replaced.
      //
      //   3. and the session is NOT merely cold-starting (Obi-Wan on #6294).
      //
      // Condition 3 is the one I got wrong first time, and it matters because
      // of what this label MEANS. `computeSessionToolBindStatus` downgrades a
      // missing child to `unknown` on `coldStartActive || !anyStdioServerLive`.
      // In the window between a respawn and Claude Code spawning its first
      // stdio child, BOTH disjuncts hold: there is no witness yet, and this
      // integration's own key is missing. Checking only the witness half
      // therefore labelled a ROUTINE COLD START as `session-not-live` - a label
      // documented to mean "needs a human at the pane", and added precisely so
      // a fleet-wide login outage would stand out from routine probe chatter.
      // A label that also fires every respawn is exactly the noise it exists to
      // cut through.
      //
      // Worth stating plainly: this PR exists to stop a false signal, and my
      // first cut of its new label produced one in the most ordinary case there
      // is. The condition below now mirrors the verdict's own disjunction, so
      // the label and the verdict cannot disagree about why this row was
      // skipped - which is what the sentence here used to claim while the code
      // did not do it.
      const missingOwnChild =
        stdio.some((k) => options.missingStdioKeys.has(k)) ||
        proxy.some((k) => options.missingStdioKeys.has(k));
      skippedDetail.push({
        definition_id: integ.definition_id,
        reason:
          !coldStartActive && options.anyStdioServerLive === false && missingOwnChild
            ? 'session-not-live'
            : 'inconclusive',
      });
      continue;
    }

    reports.push({ integration_id: integ.id, scope: integ.scope, status });

    // ENG-7318: a hard-negative verdict backed by a real child (stdio/proxy) is
    // rebindable - SIGTERM the child so Claude Code respawns it with a fresh
    // connection. Narrow to the keys that ACTUALLY failed, not every child-backed
    // key on the integration: the manager forwards these straight to
    // reapStaleMcpChildren, so including a healthy sibling child would needlessly
    // SIGTERM it (CodeRabbit, PR #2985). A dead stdio/proxy child (in
    // missingStdioKeys) drove the `missing`; a proxy whose upstream is down
    // (httpReachable=false) drove the `unreachable`. A pure-HTTP negative has no
    // child, so it contributes nothing and produces no candidate.
    if (status === 'missing' || status === 'unreachable') {
      const failingKeys = new Set<string>();
      for (const k of [...stdio, ...proxy]) {
        if (options.missingStdioKeys.has(k)) failingKeys.add(k); // dead child
      }
      for (const k of proxy) {
        if (httpReachable.get(k) === false) failingKeys.add(k); // upstream down through the proxy
      }
      // ENG-9048 AC3: and the alive-child-dead-transport case. Without this the
      // verdict would be reported and NOTHING would be restarted — the key is in
      // neither `missingStdioKeys` (its child is alive) nor a false
      // `httpReachable` (its transport is a WebSocket, not the HTTP the probe
      // exercises), so it would fall through both narrowing loops and produce an
      // `unreachable` with an empty candidate list. That is the whole feature
      // shipping as a no-op.
      if (transportDown) {
        for (const k of [...stdio, ...proxy]) {
          if (transportDown.has(k)) failingKeys.add(k);
        }
      }
      if (failingKeys.size > 0) {
        rebindCandidates.push({
          integration_id: integ.id,
          scope: integ.scope,
          status,
          serverKeys: [...failingKeys],
        });
      }
    }
  }

  return { reports, rebindCandidates, due: due.length, probed, skipped, skippedDetail, notApplicable };
}
